/**
 * Light or dark, and who decides.
 *
 * Three states, not two. "System" follows the operating system and keeps
 * following it, which is what someone who has never touched the toggle
 * expects. Choosing light or dark pins it, which is what someone who has
 * touched the toggle expects. A two-state toggle has to guess which of those
 * two people it is talking to, and is wrong for one of them.
 */

import * as React from "react"

export type Theme = "light" | "dark" | "system"

const STORAGE_KEY = "rc-tms-theme"

function systemPrefersDark() {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
}

export function storedTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === "light" || stored === "dark" ? stored : "system"
}

export function resolve(theme: Theme): "light" | "dark" {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light"
  return theme
}

/**
 * Put the class on `<html>`.
 *
 * `animate` is off for the very first call, because transitioning from the
 * boot state to the stored one makes the page visibly fade in on every load.
 */
export function applyTheme(theme: Theme, { animate = true } = {}) {
  const root = document.documentElement
  if (animate) {
    root.classList.add("theme-changing")
    window.setTimeout(() => root.classList.remove("theme-changing"), 200)
  }
  root.classList.toggle("dark", resolve(theme) === "dark")
  root.style.colorScheme = resolve(theme)
}

export function useTheme() {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    try {
      return storedTheme()
    } catch {
      // Private browsing can refuse localStorage outright, and a theme is not
      // worth failing a render over.
      return "system"
    }
  })

  React.useEffect(() => {
    applyTheme(theme, { animate: false })
  }, [theme])

  // Only while following the system: once pinned, the OS changing its mind is
  // not an instruction.
  React.useEffect(() => {
    if (theme !== "system") return
    const query = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = () => applyTheme("system", { animate: true })
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [theme])

  const setTheme = React.useCallback((next: Theme) => {
    try {
      if (next === "system") localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Unstorable is not unusable: it just will not survive a reload.
    }
    applyTheme(next, { animate: true })
    setThemeState(next)
  }, [])

  return { theme, resolved: resolve(theme), setTheme }
}
